import React, { useEffect, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { SelectContext } from '../select-context';
import { SelectCheckbox } from '../checkbox/checkbox';
import { DefaultOptionTemplate } from '../../templates/default-template';

const isOptionInView = (option, listBox) => {
  const listBoxBounding = listBox.getBoundingClientRect();
  const optionBounding = option.getBoundingClientRect();

  return (
    optionBounding.top >= listBoxBounding.top &&
    optionBounding.left >= listBoxBounding.left &&
    optionBounding.bottom <= listBoxBounding.bottom &&
    optionBounding.right <= listBoxBounding.right
  );
};

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string | {}} [value]
 * @prop {boolean} [disabled]
 * @prop {string} [label]
 * @prop {() => void} [onClick]
 * @prop {number} [optionIndex]
 * @prop {boolean} [isSelected]
 * @prop {boolean} [isCurrentSelected]
 * @prop {string} [className]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifOption = (props) => {
  const {
    children,
    value: optionValue = '',
    disabled = false,
    label = '',
    onClick = null,
    optionIndex = 0,
    isSelected = false,
    isCurrentSelected = false,
    className = '',
    ...other
  } = props;
  const [isOptionSelected, setIsOptionSelected] = useState(isSelected);
  const {
    id,
    optionSelected,
    setOptionSelected,
    selectValue,
    setSelectValue,
    setDisplayedValue,
    setIsOpen,
    selectTriggerRef,
    optionCurrent,
    setOptionCurrent,
    multiple,
    searchOption,
    totalOptions,
    setHasSelectedMultipleOption,
    onChange,
    listBoxRef
  } = useContext(SelectContext);
  const optionRef = useRef();
  const optionIsSelected = optionIndex === optionCurrent;
  const optionAtTheTop = searchOption !== '' && optionIndex === 0 && optionCurrent === -1;

  const parsedChildrenOption = childrenOption => {
    if (typeof childrenOption === 'object') {
      return React.cloneElement(children, {
        searchTerm: searchOption,
        value: label
      });
    }

    return <DefaultOptionTemplate searchTerm={searchOption} value={childrenOption} />;
  };

  useEffect(() => {
    if (optionIsSelected && !isOptionInView(optionRef.current, listBoxRef.current)) {
      optionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [optionIsSelected]);

  useEffect(() => {
    setIsOptionSelected(isSelected);
  }, [isSelected]);

  const handleClickSingle = () => {
    setSelectValue(optionValue);
    setDisplayedValue(typeof children === 'object' ? label : children);
    setOptionSelected(optionIndex);
    setOptionCurrent(optionIndex);
    setIsOpen(false);
    selectTriggerRef.current.focus();

    if (onChange) {
      onChange(optionValue);
    }
  };

  const handleClickMultiple = () => {
    setSelectValue(prevState => {
      if (prevState.includes(optionValue)) {
        const filteredValues = prevState.filter(val => val !== optionValue);
        if (onChange) {
          onChange(filteredValues);
        }

        return filteredValues;
      }
      if (onChange) {
        onChange([...prevState, optionValue]);
      }

      return [...prevState, optionValue];
    });

    setDisplayedValue(prevState => {
      const valueShowed = typeof children === 'object' ? label : children;
      if (prevState.includes(valueShowed)) {
        return prevState.filter(val => val !== valueShowed);
      }

      return [...prevState, valueShowed];
    });

    setOptionSelected(prevState => {
      if (prevState.includes(optionIndex)) {
        return prevState.filter(val => val !== optionIndex);
      }

      return [...prevState, optionIndex];
    });

    setHasSelectedMultipleOption(true);
    setIsOptionSelected(!isOptionSelected);
  };

  const handleClick = () => {
    if (!disabled) {
      if (multiple) {
        handleClickMultiple();
      } else {
        handleClickSingle();
      }

      if (onClick) {
        onClick();
      }
    }
  };

  const classes = classNames({
    'motif-option': true,
    'motif-option-disabled': disabled,
    'motif-option-current': optionIsSelected || isCurrentSelected || optionAtTheTop,
    [className]: true
  });

  return (
    <div
      {...other}
      id={`${id}-${optionIndex}`}
      ref={optionRef}
      role="option"
      aria-selected={multiple ? isSelected : optionIndex === optionSelected}
      tabIndex={optionIsSelected || isCurrentSelected ? '0' : '-1'}
      aria-disabled={disabled}
      className={classes}
      onClick={handleClick}
      data-motif-select-option
    >
      {multiple && totalOptions > 0 && <SelectCheckbox selected={isOptionSelected} />}
      {parsedChildrenOption(children)}
    </div>
  );
};

MotifOption.propTypes = {
  children: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  optionIndex: PropTypes.number,
  isSelected: PropTypes.bool,
  isCurrentSelected: PropTypes.bool,
  className: PropTypes.string
};

export { MotifOption };
